import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  changeMyPassword,
  createUser,
  getMe,
  listMembers,
  listUsers,
  resendInvite,
  updateMe,
  updateUser,
  uploadMyPhoto,
  uploadUserPhoto,
  type CreateUserInput,
  type UpdateProfileInput,
  type UpdateUserInput,
} from '@/api/users'
import { useAuthStore } from '@/features/auth/authStore'
import {
  addTeamMember,
  createTeam,
  deleteTeam,
  listTeams,
  removeTeamMember,
  setTeamLead,
  updateTeam,
  type TeamInput,
} from '@/api/teams'

export function useUsers(enabled = true) {
  return useQuery({ queryKey: ['users'], queryFn: listUsers, enabled })
}

/** Lightweight roster ({id, fullName, role}) available to any authenticated user. */
export function useMembers() {
  return useQuery({ queryKey: ['members'], queryFn: listMembers })
}

export function useCreateUser() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: CreateUserInput) => createUser(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users'] })
      qc.invalidateQueries({ queryKey: ['members'] }) // new member appears in owner pickers/cards
    },
  })
}

export function useResendInvite() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => resendInvite(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  })
}

export function useUpdateUser() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateUserInput }) => updateUser(id, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users'] })
      qc.invalidateQueries({ queryKey: ['teams'] }) // member names may change
      // The roster ({id, fullName, role, email, phone, status}) feeds the owner card/pickers,
      // so a changed work phone/email/role must refresh it too.
      qc.invalidateQueries({ queryKey: ['members'] })
    },
  })
}

export function useUploadUserPhoto() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, file }: { id: string; file: File }) => uploadUserPhoto(id, file),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users'] })
      qc.invalidateQueries({ queryKey: ['members'] }) // the avatar shows on owner cards
    },
  })
}

export function useTeams() {
  return useQuery({ queryKey: ['teams'], queryFn: listTeams })
}

export function useCreateTeam() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: TeamInput) => createTeam(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['teams'] }),
  })
}

export function useUpdateTeam() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: TeamInput }) => updateTeam(id, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['teams'] }),
  })
}

export function useDeleteTeam() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => deleteTeam(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['teams'] }),
  })
}

export function useAddTeamMember() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ teamId, userId }: { teamId: string; userId: string }) => addTeamMember(teamId, userId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['teams'] }),
  })
}

export function useRemoveTeamMember() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ teamId, userId }: { teamId: string; userId: string }) => removeTeamMember(teamId, userId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['teams'] }),
  })
}

export function useSetTeamLead() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ teamId, userId, lead }: { teamId: string; userId: string; lead: boolean }) =>
      setTeamLead(teamId, userId, lead),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['teams'] }),
  })
}

/** The signed-in user's own profile (My profile). */
export function useMe() {
  return useQuery({ queryKey: ['me'], queryFn: getMe })
}

/**
 * Save My profile. Also refreshes the signed-in identity (sidebar name, the composer's signature
 * preview) and the roster that owner cards read — no re-login needed to see the change.
 */
export function useUpdateMe() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: UpdateProfileInput) => updateMe(input),
    onSuccess: (u) => {
      qc.setQueryData(['me'], u)
      qc.invalidateQueries({ queryKey: ['members'] })
      qc.invalidateQueries({ queryKey: ['users'] })
      const { user, agency, refreshIdentity } = useAuthStore.getState()
      if (user) refreshIdentity({ ...user, fullName: u.fullName, emailSignature: u.emailSignature }, agency)
    },
  })
}

export function useUploadMyPhoto() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (file: File) => uploadMyPhoto(file),
    onSuccess: (u) => {
      qc.setQueryData(['me'], u)
      qc.invalidateQueries({ queryKey: ['members'] })
    },
  })
}

export function useChangeMyPassword() {
  return useMutation({
    mutationFn: ({ current, next }: { current: string; next: string }) => changeMyPassword(current, next),
  })
}
